import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import type { Formula1Race } from '../../types/formula1'

interface ManageRaceModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  race: Formula1Race | null
  onSaved: (updated: Formula1Race) => void
  onSave: (raceId: string, data: { actualResults: Record<string, number>; status?: string }) => Promise<Formula1Race>
}

const POSITIONS = Array.from({ length: 22 }, (_, i) => i + 1)

export function ManageRaceModal({
  open,
  onOpenChange,
  race,
  onSaved,
  onSave,
}: ManageRaceModalProps) {
  const [loading, setLoading] = useState(false)
  const drivers = race && Array.isArray(race.drivers) ? race.drivers : []
  const [positionsByDriver, setPositionsByDriver] = useState<Record<string, number>>({})

  useEffect(() => {
    if (open && race?.actualResults && typeof race.actualResults === 'object') {
      setPositionsByDriver({ ...race.actualResults })
    } else if (open && drivers.length) {
      setPositionsByDriver({})
    }
  }, [open, race?.actualResults, drivers.length])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!race?.id) return
    const actualResults: Record<string, number> = {}
    drivers.forEach((d) => {
      const pos = positionsByDriver[d]
      if (pos != null && pos >= 1) actualResults[d] = pos
    })
    if (Object.keys(actualResults).length === 0) {
      toast.error('Set at least one driver position')
      return
    }
    const positions = Object.values(actualResults)
    if (new Set(positions).size !== positions.length) {
      toast.error('Each finishing position can only be used once')
      return
    }
    setLoading(true)
    try {
      const updated = await onSave(race.id, { actualResults, status: 'completed' })
      onSaved(updated)
      onOpenChange(false)
      toast.success('Race results updated')
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Update failed')
    } finally {
      setLoading(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => onOpenChange(false)}>
      <div className="w-full max-w-md glass-strong rounded-2xl p-5 sm:p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-display font-bold text-foreground">
            Race {race?.day}: {race?.city}{race?.country ? `, ${race.country}` : ''}
          </h2>
          <button type="button" onClick={() => onOpenChange(false)} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground">×</button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Set finishing position (1–22) for each driver.</p>
        <form onSubmit={handleSubmit} className="space-y-3">
          {drivers.map((driver) => (
            <div key={driver} className="flex items-center justify-between gap-2">
              <span className="text-sm text-foreground truncate flex-1">{driver}</span>
              <select
                className="w-20 px-2 py-1.5 rounded border border-border bg-background text-foreground text-sm"
                value={positionsByDriver[driver] ?? ''}
                onChange={(e) => {
                  const v = e.target.value
                  setPositionsByDriver((prev) => {
                    const next = { ...prev }
                    if (v) next[driver] = Number(v)
                    else delete next[driver]
                    return next
                  })
                }}
              >
                <option value="">—</option>
                {POSITIONS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
          ))}
          <div className="flex gap-2 pt-4">
            <button type="button" className="px-4 py-2 rounded-lg border border-border hover:bg-muted/50" onClick={() => onOpenChange(false)}>Cancel</button>
            <button type="submit" className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50" disabled={loading}>{loading ? 'Saving...' : 'Save results'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}
